import { ArrowRight, XCircle, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/hooks/useLanguage";

const WarumWissensdatenbankSection = () => {
  const { ref, isVisible } = useScrollAnimation();
  const { t, tArray, localizePath } = useLanguage();
  const problems = tArray("knowledgeBasePage.warum.problems");
  const solutions = tArray("knowledgeBasePage.warum.solutions");

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      className={cn(
        "py-16 md:py-24 bg-secondary/50 transition-all duration-700 ease-out",
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      )}
    >
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <Badge variant="outline" className="mb-4 text-sm font-semibold text-primary uppercase tracking-wider">
              {t("knowledgeBasePage.warum.badge")}
            </Badge>

            <h2 className="text-3xl md:text-4xl font-semibold mb-2">
              <span className="text-foreground">{t("knowledgeBasePage.warum.title")}</span>
            </h2>

            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              {t("knowledgeBasePage.warum.intro")}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-10">
            <div
              className={cn(
                "bg-background rounded-2xl p-8 border border-border transition-all duration-500",
                isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
              )}
              style={{ transitionDelay: isVisible ? "200ms" : "0ms" }}
            >
              <h3 className="text-lg font-semibold text-primary mb-6 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-chathero-orange/10 flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-chathero-orange" />
                </span>
                {t("knowledgeBasePage.warum.problemTitle")}
              </h3>
              <ul className="space-y-4">
                {problems.map((problem, index) => (
                  <li
                    key={index}
                    className={cn(
                      "flex items-start gap-3 transition-all",
                      isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4"
                    )}
                    style={{ transitionDelay: isVisible ? `${300 + index * 100}ms` : "0ms", transitionDuration: "400ms" }}
                  >
                    <span className="w-6 h-6 rounded-full bg-chathero-orange/10 flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-bold text-chathero-orange">{index + 1}</span>
                    <span className="text-muted-foreground">{problem}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div
              className={cn(
                "bg-background rounded-2xl p-8 border border-chathero-mint/30 shadow-[0_4px_24px_rgba(0,196,180,0.08)] transition-all duration-500",
                isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"
              )}
              style={{ transitionDelay: isVisible ? "200ms" : "0ms" }}
            >
              <h3 className="text-lg font-semibold text-primary mb-6 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-chathero-mint/10 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-chathero-mint" />
                </span>
                {t("knowledgeBasePage.warum.solutionTitle")}
              </h3>
              <ul className="space-y-4">
                {solutions.map((solution, index) => (
                  <li
                    key={index}
                    className={cn(
                      "flex items-start gap-3 transition-all",
                      isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-4"
                    )}
                    style={{ transitionDelay: isVisible ? `${300 + index * 100}ms` : "0ms", transitionDuration: "400ms" }}
                  >
                    <span className="w-6 h-6 rounded-full bg-chathero-mint/10 flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-bold text-chathero-mint">{index + 1}</span>
                    <span className="text-muted-foreground">{solution}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div
            className={cn(
              "bg-chathero-mint/10 border-l-4 border-chathero-mint rounded-r-xl p-6 md:p-8 mb-8 transition-all duration-500",
              isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"
            )}
            style={{ transitionDelay: isVisible ? "600ms" : "0ms" }}
          >
            <p className="text-xl md:text-2xl font-semibold text-primary">
              {t("knowledgeBasePage.warum.highlight")}
            </p>
          </div>

          <Link
            to={localizePath("/produkte/ai-agent")}
            className={cn(
              "inline-flex items-center gap-2 text-chathero-mint font-semibold hover:gap-3 transition-all",
              isVisible ? "opacity-100" : "opacity-0"
            )}
            style={{ transitionDelay: isVisible ? "700ms" : "0ms" }}
          >
            {t("knowledgeBasePage.warum.linkText")}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default WarumWissensdatenbankSection;
